import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Grid,
  Rating,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import { motion } from 'framer-motion';

/**
 * QuickViewModal Component
 * Quick preview of package details in a modal
 */
const QuickViewModal = ({ open, onClose, package: pkg = {} }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  // Return null if modal is not open or package is not provided
  if (!open || !pkg || !pkg.id) return null;

  const images = pkg.images || (pkg.image ? [pkg.image] : ['/placeholder.jpg']);
  const amenities = pkg.amenities || [
    'WiFi',
    'Air Conditioning',
    'Private Bathroom',
    'Daily Breakfast',
    'Guided Tours',
    '24/7 Support',
  ];

  const handleShare = () => {
    const text = `Check out this amazing package: ${pkg.title || pkg.name || 'Umrah Package'}`;
    if (navigator.share) {
      navigator.share({
        title: pkg.title || pkg.name || 'Umrah Package',
        text,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
        >
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Grid container spacing={0}>
            {/* Image Section */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '100%',
                  backgroundColor: '#f5f7fa',
                  overflow: 'hidden',
                }}
              >
                <motion.img
                  key={activeImage}
                  src={images[activeImage] || pkg.image || '/placeholder.jpg'}
                  alt={pkg.title || pkg.name || 'Package'}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />

                {/* Image Thumbnails */}
                {images.length > 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      left: 12,
                      right: 12,
                      display: 'flex',
                      gap: 1,
                      overflow: 'auto',
                    }}
                  >
                    {images.map((img, idx) => (
                      <motion.img
                        key={idx}
                        src={img}
                        alt={`${pkg.title || pkg.name || 'Package'} ${idx}`}
                        onClick={() => setActiveImage(idx)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: activeImage === idx ? '3px solid #003B95' : '2px solid transparent',
                          objectFit: 'cover',
                          transition: 'border 0.2s',
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Details Section */}
            <Grid item xs={12} md={6} sx={{ padding: 3 }}>
              {/* Badges */}
              <Box sx={{ display: 'flex', gap: 1, marginBottom: 2 }}>
                {pkg.badges?.map((badge, idx) => (
                  <Chip
                    key={idx}
                    label={badge.label}
                    size="small"
                    sx={{
                      backgroundColor: badge.color,
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>

              {/* Title */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  marginBottom: 1,
                  color: 'text.primary',
                }}
              >
                {pkg.title || pkg.name || 'Umrah Package'}
              </Typography>

              {/* Rating */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                <Rating value={pkg.rating || pkg.averageRating || 0} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">
                  {pkg.rating || pkg.averageRating || 0} ({pkg.reviews || 0} reviews)
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2, lineHeight: 1.6 }}
              >
                {pkg.description || pkg.summary || 'Experience a spiritually fulfilling journey.'}
              </Typography>

              {/* Price & Duration */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 59, 149, 0.05)',
                  padding: 2,
                  borderRadius: '8px',
                  marginBottom: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Starting from
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    {(pkg.currency === 'PKR' ||
                      pkg.category === 'Pakistan' ||
                      pkg.category === 'Local' ||
                      /pakistan|swat|hunza|skardu|murree|lahore|naran/i.test((pkg.title || pkg.name || '') + ' ' + (pkg.description || ''))
                    )
                      ? `PKR ${Number(pkg.price || pkg.basePrice || 0).toLocaleString()}`
                      : `$${Number(pkg.price || pkg.basePrice || 0).toLocaleString()}`}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                    }}
                  >
                    {pkg.duration || 0} Days
                  </Typography>
                </Box>
              </Box>

              {/* Amenities */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: 1,
                  color: 'text.primary',
                }}
              >
                What's Included
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: 2 }}>
                {amenities.map((amenity, idx) => (
                  <Chip
                    key={idx}
                    label={amenity}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    padding: '10px 16px',
                    borderRadius: '6px',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(0, 59, 149, 0.3)',
                    },
                  }}
                >
                  Book Now
                </Button>
                <IconButton
                  onClick={() => setIsFavorite(!isFavorite)}
                  sx={{
                    color: isFavorite ? '#FF1744' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 23, 68, 0.1)',
                    },
                  }}
                >
                  {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
                <IconButton
                  onClick={handleShare}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 59, 149, 0.1)',
                    },
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewModal;
